// apps/admin/src/features/shipping/components/ShippingMenuGroup.tsx
//
// Phase 4.7 Amendment #3 Wave 2 Task 18 — Sidebar `Shipping` menu group
// definition + the shared route placeholder used by the four child
// route stubs until later waves (19/20/21/22) land their real bodies.
//
// The single `/shipping` 7-tab shell (ShippingPage.tsx) is
// SUPERSEDED by a sidebar menu group `Shipping` (base gating MANAGE_SHIPPING)
// that expands to FOUR child routes:
//   - Shipping Price   → /shipping/price          (MANAGE_SHIPPING)
//   - Track Routing    → /shipping/track-routing   (MANAGE_SHIPPING)
//   - Config           → /shipping/config          (MANAGE_SHIPPING)
//   - Rate Table       → /shipping/rate-table       (VIEW_FINANCIAL_COST —
//                         whole link HIDDEN without VFC;).
//
// FE-hide is UX-only; the API stays authoritative.
// All labels resolve from the `navigation` namespace (VN primary) — no
// hardcoded sidebar strings. AdminSidebar.tsx imports `SHIPPING_MENU_GROUP`
// and the child-link descriptors so the group declaration lives in the
// shipping feature dir rather than inline in the shell.
//
// vg-binding: CONTEXT:decisions=P4.7.D-51,P4.7.D-23,P4.7.D-24,P4.7.D-17
// vg-binding: INTERFACE-STANDARDS:error-shape

import type { JSX } from 'react';

import { useTranslation } from '../../../i18n/useTranslation.js';
import { PERMISSIONS } from '../../../lib/permissions.js';

// ──────────────────────────────────────────────────────────────────────
// Menu-group descriptors — consumed by AdminSidebar.tsx (`kind: 'group'`).
// Each child carries the stable `data-testid` suffix the PLAN <test_ids>
// require (shipping-nav-price / -track-routing / -config / -rate-table) and
// the codegen group toggle id (shipping-nav-group).
// ──────────────────────────────────────────────────────────────────────

export interface ShippingChildLink {
  /** Stable key → data-testid `${testId}` (verbatim from PLAN <test_ids>). */
  key: string;
  /** `navigation` namespace label key. */
  labelKey: string;
  /** Child route path. */
  to: string;
  /** Stable e2e selector — verbatim from PLAN <test_ids>. */
  testId: string;
  /**
   * Permission gate for THIS link. Rate Table is gated VIEW_FINANCIAL_COST so
   * the whole link is hidden without the cost grant; the
   * others inherit the group base MANAGE_SHIPPING.
   */
  permission: string;
  /**
   * Exact-match only — the link activates ONLY on its own pathname, never on a
   * deeper route. Required whenever `to` is a PREFIX of its own sibling
   * submenus: `/shipping/rate-table-2` prefixes
   * `/shipping/rate-table-2/{routing,margin,history,rate}`, so the sidebar's
   * default prefix match (`AdminSidebar.isLeafActive`) lit BOTH the parent
   * "Rate Table 2" row AND the active submenu ("Bảng giá" = rt2Rate) at once —
   * and kept the parent lit while browsing every other rt2 submenu.
   * dbg-20260730-072612 (same bug class as dbg-20260529-2009).
   */
  exact?: boolean;
}

/** Group-level identity (base gating MANAGE_SHIPPING). */
export const SHIPPING_MENU_GROUP = {
  key: 'shipping',
  /** `navigation:shipping.group` label key. */
  labelKey: 'shipping.group',
  /** Group toggle e2e selector (PLAN <test_ids>: shipping-nav-group). */
  groupTestId: 'shipping-nav-group',
  /** Base permission gating the whole group. */
  permission: PERMISSIONS.MANAGE_SHIPPING as string,
} as const;

/** Four child route links, in canonical sidebar order. */
export const SHIPPING_CHILD_LINKS: readonly ShippingChildLink[] = [
  {
    key: 'shipping.price',
    labelKey: 'shipping.price',
    to: '/shipping/price',
    testId: 'shipping-nav-price',
    permission: PERMISSIONS.MANAGE_SHIPPING,
  },
  {
    key: 'shipping.trackRouting',
    labelKey: 'shipping.trackRouting',
    to: '/shipping/track-routing',
    testId: 'shipping-nav-track-routing',
    permission: PERMISSIONS.MANAGE_SHIPPING,
  },
  {
    key: 'shipping.config',
    labelKey: 'shipping.config',
    to: '/shipping/config',
    testId: 'shipping-nav-config',
    permission: PERMISSIONS.MANAGE_SHIPPING,
  },
  {
    // Canonical Rate Table entry — the Phase 4.8 master-detail Rate page
    // (/shipping/rate-table-2/rate). The legacy 4.7 /shipping/rate-table link
    // and the redundant bare "Rate Table 2" parent link (which only redirected
    // here) were dropped so the sidebar carries a single Rate Table item; the
    // old route now redirects to this one. Gated VIEW_FINANCIAL_COST — whole
    // link hidden without VFC; FE-hide is UX-only, the API stays authoritative.
    key: 'shipping.rt2Rate',
    labelKey: 'shipping.rt2Rate',
    to: '/shipping/rate-table-2/rate',
    testId: 'shipping-nav-rt2-rate',
    permission: PERMISSIONS.VIEW_FINANCIAL_COST,
    // Prefix of the four rt2* submenus below → exact-match only, otherwise this
    // row co-highlights with whichever submenu is open. dbg-20260730-072612.
    exact: true,
  },
  // Phase 4.8 Amendment #1 CR-1 (Task 06) — the 3 big top-level tabs inside
  // Rate Table 2 (Routing / Margin-report / History) are split into SEPARATE
  // sidebar submenus, each with its own native route; Rate becomes an
  // independent submenu keeping its inner sub-tabs (P4.8.D-06). Each is gated
  // the SAME way as the parent Rate Table 2 entry (dual VFC + MANAGE_SHIPPING;
  // link uses VFC here, the page HARD-blocks on both per P4.8.D-21) — whole
  // link hidden without VIEW_FINANCIAL_COST; FE-hide is UX-only, the API stays
  // authoritative. Labels resolve from the Task-09 `navigation:shipping.rt2*`
  // keys (P4.8.D-19 — no hardcoded string). Native routes give per-submenu
  // deep-link + back/forward for free (P4.8.D-08).
  // vg-binding: CONTEXT:decisions=P4.8.D-06,P4.8.D-08,P4.8.D-19
  {
    key: 'shipping.rt2Routing',
    labelKey: 'shipping.rt2Routing',
    to: '/shipping/rate-table-2/routing',
    testId: 'shipping-nav-rt2-routing',
    permission: PERMISSIONS.VIEW_FINANCIAL_COST,
  },
  {
    key: 'shipping.rt2Margin',
    labelKey: 'shipping.rt2Margin',
    to: '/shipping/rate-table-2/margin',
    testId: 'shipping-nav-rt2-margin',
    permission: PERMISSIONS.VIEW_FINANCIAL_COST,
  },
  {
    key: 'shipping.rt2History',
    labelKey: 'shipping.rt2History',
    to: '/shipping/rate-table-2/history',
    testId: 'shipping-nav-rt2-history',
    permission: PERMISSIONS.VIEW_FINANCIAL_COST,
  },
] as const;

// ──────────────────────────────────────────────────────────────────────
// Shared route placeholder — rendered by the four child route stubs
// (ShippingPriceRoute / ConfigPage / RateTablePage / TrackRoutingPage) until
// later waves replace each body. Copy resolves from the `navigation`
// namespace — no hardcoded strings.
// ──────────────────────────────────────────────────────────────────────

export interface ShippingRoutePlaceholderProps {
  /** Route key — drives the data-testid suffix. */
  routeKey: string;
  /** `navigation:shipping.*` label key for the route heading. */
  titleKey: string;
  /** Stable e2e selector for the placeholder surface. */
  testId: string;
}

export function ShippingRoutePlaceholder({
  routeKey,
  titleKey,
  testId,
}: ShippingRoutePlaceholderProps): JSX.Element {
  const { t } = useTranslation('navigation');
  return (
    <section
      aria-labelledby={`shipping-route-${routeKey}-heading`}
      data-testid={testId}
      className="flex min-h-[40vh] flex-col items-start gap-2 rounded-md border border-dashed border-pw-border bg-pw-surface p-8"
    >
      <h1
        id={`shipping-route-${routeKey}-heading`}
        className="text-2xl font-semibold text-pw-text"
      >
        {t(titleKey)}
      </h1>
      <p className="text-sm text-pw-text-muted">{t('shipping.comingSoon')}</p>
    </section>
  );
}

export default ShippingRoutePlaceholder;
